import { useState } from "react";
import { Upload, X } from "lucide-react";
import { apiUpload, resolveMediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function MediaUploadField({
    label,
    value,
    onChange,
    accept,
    relatedId,
    relatedType = "profile",
    help = "Choose a file to upload, or keep using an external URL below.",
    preview = false,
}) {
    const [uploading, setUploading] = useState(false);
    const [filename, setFilename] = useState("");

    const upload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        setFilename(file.name);
        setUploading(true);
        try {
            const result = await apiUpload("/files/upload", file, {
                related_type: relatedType,
                related_id: relatedId,
            });
            onChange(result.public_url || result.url || "");
            toast.success(`${file.name} uploaded`);
        } catch (error) {
            setFilename("");
            toast.error(error?.response?.data?.detail || "Could not upload that file");
        } finally {
            setUploading(false);
        }
    };

    const clear = () => {
        setFilename("");
        onChange("");
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium">{label}</span>
                {value && <Button type="button" variant="ghost" size="sm" onClick={clear}><X className="mr-1 h-3.5 w-3.5" />Remove</Button>}
            </div>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-border bg-muted/20 px-3 py-3 text-sm hover:bg-muted/40">
                <Upload className="h-4 w-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1 truncate">{uploading ? "Uploading…" : filename || "Choose a file"}</span>
                <input type="file" className="sr-only" accept={accept} onChange={upload} disabled={uploading} />
            </label>
            <p className="text-xs text-muted-foreground">{help}</p>
            {preview && value && <img src={resolveMediaUrl(value)} alt="Uploaded preview" className="h-24 w-full rounded-lg border border-border object-cover" />}
        </div>
    );
}
